import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './TransactionsSection.styles';
import { Operation } from './Transactions.types';
import {
  ACCOUNT_TAB,
  ADS_TAB,
  DEFAULT_PAGE_SIZE,
  TransactionTabType,
} from './Transactions.constants';
import {
  mapPaymentToOperation,
  mapTransactionToOperation,
} from './Transactions.helpers';
import { MOCK_OPERATIONS } from './Transactions.mocks';
import { OperationsList } from './components/OperationsList';
import { OperationsTable } from './components/OperationsTable';
import { TransactionsTabs } from './components/TransactionsTabs';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { useUserPayments, useUserTransactions } from '@/features/Profile/hooks';

function getActiveTab(hash: string): TransactionTabType {
  return hash === ACCOUNT_TAB || hash === '' ? ACCOUNT_TAB : ADS_TAB;
}

// TODO: remove mock data before merging; set to false to use real API requests
const USE_MOCK_DATA = true;

export const TransactionsSection = () => {
  const { t } = useTranslation();
  const { hash: tabType } = useUrlAndHashFromPath();
  const [, setSelectedOperation] = useState<Operation | null>(null);

  const activeTab = getActiveTab(tabType);
  const isAccountTab = activeTab === ACCOUNT_TAB;

  const paymentsQuery = useUserPayments({
    variables: { first: DEFAULT_PAGE_SIZE },
    skip: isAccountTab || USE_MOCK_DATA,
  });

  const transactionsQuery = useUserTransactions({
    variables: { first: DEFAULT_PAGE_SIZE },
    skip: !isAccountTab || USE_MOCK_DATA,
  });

  const operations = useMemo(() => {
    if (USE_MOCK_DATA) {
      return MOCK_OPERATIONS;
    }

    if (isAccountTab) {
      return transactionsQuery.transactions.map(mapTransactionToOperation);
    }

    return paymentsQuery.payments
      .filter(payment => payment.ad)
      .map(mapPaymentToOperation);
  }, [isAccountTab, paymentsQuery.payments, transactionsQuery.transactions]);

  function handleShowDetails(operation: Operation) {
    setSelectedOperation(operation);
  }

  return (
    <S.Container>
      <S.Title>{t('pages.cabinet.operations_history')}</S.Title>
      <TransactionsTabs activeTab={activeTab} />

      <S.DesktopOnly>
        <OperationsTable
          operations={operations}
          onShowDetails={handleShowDetails}
        />
      </S.DesktopOnly>

      <S.MobileOnly>
        <OperationsList
          operations={operations}
          onShowDetails={handleShowDetails}
        />
      </S.MobileOnly>
    </S.Container>
  );
};
